import { faCircleCheck } from "@fortawesome/pro-regular-svg-icons/faCircleCheck";
import { faCircleEllipsis } from "@fortawesome/pro-regular-svg-icons/faCircleEllipsis";
import { faCircleX } from "@fortawesome/pro-regular-svg-icons/faCircleX";
import { faCommentEdit } from "@fortawesome/pro-regular-svg-icons/faCommentEdit";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Button, Divider, Grid, IconButton, Menu, MenuItem } from "@mui/material";
import { MprcRequestErrorDialog, MprcRequestIndicator } from "components";
import { MprcFeedTypography } from "components/MprcFeedTypography";
import { PublicationActionResponse } from "generated/CoreApi_v1";
import { useCoreApi } from "providers/CoreApiProvider";
import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { SessionService } from "services/sessionService";
import { AdjustPublicationDialogResult, AdjustPublicationDrawer } from "views/knowledge/components/card/actions/AdjustPublicationDrawer";
import { PublicationLogDialog } from "views/knowledge/components/card/actions/PublicationLogDialog";
import { DeletePublicationFactory } from "views/knowledge/components/card/behaviors/common/DeletePublicationFactory";
import { EditPublicationFactory } from "views/knowledge/components/card/behaviors/common/EditPublicationFactory";
import { useCardContext } from "../../CardBuilder";


///
const ModeratePublicationsOptions: React.FC = () => {
    const { t } = useTranslation("knowledge");

    const [anchor, setAnchor] = useState<Element | null>(null);
    const [dialog, setDialog] = useState<any>(null);

    const {
        pub: {
            publicationId,
            publicationType,
            masterPublication,
            title,
            isDraft,
            interaction: {
                allowDelete,
                allowEdit,
                allowDownloadVideoOrAudio,
                allowViewHistory,
            }
        },
        cardOwnProps: {
            presentation: {
                currentIsVideo,
                currentAttachId
            }
        },
        onFeedItemChanged,
        onReload
    } = useCardContext();

    type MenuOptions =
        | "edit"
        | "delete"
        | "download"
        | "history"
        ;

    type MenuItem = { key: MenuOptions, label: string; };

    const closeDialog = () => {
        setDialog(null);
    };

    // Opções do menu
    const menuItems = useMemo(() => {
        var arr: React.ReactNode[] = [];

        const groupOptions: Array<Array<MenuItem>> = [];

        const groupItems1: MenuItem[] = [];
        if (allowEdit) groupItems1.push({ key: "edit", label: t("Feed.Card.Options.EditAction") });
        if (groupItems1.length > 0) groupOptions.push(groupItems1);

        const groupItems2: MenuItem[] = [];
        if (currentAttachId && currentIsVideo && allowDownloadVideoOrAudio) groupItems2.push({ key: "download", label: t("Feed.Card.Options.DownloadAction") });
        if (allowViewHistory) groupItems2.push({ key: "history", label: t("Feed.Card.Options.HistoryAction") });
        if (groupItems2.length > 0) groupOptions.push(groupItems2);

        const groupItems99: MenuItem[] = [];
        if (allowDelete) groupItems99.push({ key: "delete", label: t("Feed.Card.Options.RemoveAction") });
        if (groupItems99.length > 0) groupOptions.push(groupItems99);

        groupOptions.forEach((g, gindex) => {

            g.forEach((v) => {
                arr.push(<MenuItem
                    key={v.key}
                    sx={(theme) => ({ color: v.key == "delete" ? theme.palette.error.main : undefined })}
                    onClick={
                        () => {
                            handleChooseMenuItem(v.key);
                            setAnchor(null);
                        }
                    }
                >
                    {v.label}
                </MenuItem>);
            });

            if (gindex < groupOptions.length - 1)
                arr.push(<Divider key={gindex} />);
        });

        return arr;
    }, []);

    ///
    const handleChooseMenuItem = (option: MenuOptions) => {
        setAnchor(null);
        switch (option) {
            case "edit":
                setDialog(<EditPublicationFactory
                    publicationId={publicationId}
                    publicationType={publicationType}
                    masterPublicationId={masterPublication?.publicationId}
                    masterTitle={title}
                    onClose={(success) => { setDialog(null); if (success) onReload(); }}
                />);
                break;

            case "delete":
                setDialog(<DeletePublicationFactory
                    publicationId={publicationId}
                    title={title}
                    isDraft={isDraft}
                    publicationType={publicationType}
                    isThread={masterPublication != null}
                    onClose={
                        (deleted) => {
                            if (deleted) onFeedItemChanged({ deletedOn: new Date() });
                            setDialog(null);
                        }
                    }
                />);
                break;

            case "history":
                setDialog(<PublicationLogDialog
                    publicationId={publicationId}
                    title={title}
                    onClose={closeDialog}
                />);
                break;

            case "download":
                SessionService.downloadWithToken(
                    `/api/v1/KnowledgePublication/DownloadOriginalMedia?attachId=${currentAttachId!}`
                    , `${title}.txt`);
                break;
        }
    };

    return (
        <>
            {dialog}
            <MprcFeedTypography variant="action">
                <IconButton color="inherit" onClick={(e) => setAnchor(e.currentTarget)}>
                    <FontAwesomeIcon icon={faCircleEllipsis} fixedWidth />
                </IconButton>
                <Menu
                    anchorEl={anchor}
                    open={anchor != null}
                    onClose={() => setAnchor(null)}
                >
                    {menuItems}
                </Menu>
            </MprcFeedTypography>
        </>
    );
};

///
export const ModeratePublicationsFooter = () => {
    const {
        pub: { title, moderatePublication },
        onFeedItemChanged
    } = useCardContext();

    const api = useCoreApi();
    const { t } = useTranslation("knowledge");
    const [saving, setSaving] = useState<boolean>(false);
    const [error, setError] = useState<any>(null);
    const [showDialog, setShowDialog] = useState<boolean>(false);

    const execute = async (actionResponse: PublicationActionResponse, messageToAuthor: string | null = null, messageToRequester: string | null = null) => {
        setSaving(true);

        try {
            var result = await api.v1.knowledgeManagement.moderatePublication({ actionId: moderatePublication!.actionId, actionResponse, messageToAuthor, messageToRequester });
            if (result.data === true) onFeedItemChanged({ moderatePublication: null });
        }
        catch (e) {
            setError(e);
        }
        finally {
            setSaving(false);
        }
    };

    const handleClose = async (dialogResult: AdjustPublicationDialogResult | undefined) => {
        setShowDialog(false);
        if (dialogResult) execute(dialogResult.status, null, dialogResult.message);
    };

    return (
        <>
            <MprcRequestIndicator type="post" visible={saving} />

            <MprcRequestErrorDialog error={error} onClose={() => setError(null)} />

            {
                showDialog &&
                <AdjustPublicationDrawer
                    title={title}
                    onClose={handleClose}
                />
            }

            <Grid container justifyContent="space-between" alignItems="center">
                <Grid>
                    <Grid container spacing={3} alignItems="center" justifyContent="center">
                        <Grid>
                            <Button variant="text" color="success" onClick={() => execute(PublicationActionResponse.ApprovePublication)}>
                                {<FontAwesomeIcon icon={faCircleCheck} fixedWidth />}
                                <span>{t("Feed.Card.ModeratePublications.ApproveButtonText")}</span>
                            </Button>
                        </Grid>
                        <Grid>
                            <Button variant="text" color="error" onClick={() => execute(PublicationActionResponse.RejectPublication)}>
                                {<FontAwesomeIcon icon={faCircleX} fixedWidth />}
                                <span>{t("Feed.Card.ModeratePublications.RejectButtonText")}</span>
                            </Button>
                        </Grid>
                        <Grid>
                            <Button variant="text" color="muted" onClick={() => setShowDialog(true)}>
                                {<FontAwesomeIcon icon={faCommentEdit} fixedWidth />}
                                <span>{t("Feed.Card.ModeratePublications.AdjustButtonText")}</span>
                            </Button>
                        </Grid>
                    </Grid>
                </Grid>
                <Grid>
                    <ModeratePublicationsOptions />
                </Grid>
            </Grid>
        </>
    );
};
